/**
 * An order's legs with their resolved tractor/driver relations and the active
 * EAPI trip mirrored onto each leg (`eapi_trip_id`, `eapi_status`).
 *
 * Shared by the order detail page and the order preview sheet so both read
 * one cache entry: a leg can't show a different status on the two surfaces,
 * and every invalidation of `["order-children-legs", orderId]` (EAPI sync,
 * confirm, status transitions, edits) refreshes both. Resolve the display
 * status from these rows with `resolveLegStatus`, never inline.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export type OrderLeg = Tables<"legs"> & {
  assigned_tractor: Tables<"tractors"> | null;
  assigned_operator: Tables<"drivers"> | null;
  assigned_operator_2: Tables<"drivers"> | null;
  eapi_trip_id: string | null;
  eapi_status: string | null;
};

export const orderLegsQueryKey = (orderId: string | null | undefined) =>
  ["order-children-legs", orderId] as const;

export async function fetchOrderLegs(orderId: string): Promise<OrderLeg[]> {
  const { data, error } = await supabase
    .from("legs")
    .select("*")
    .eq("order_id", orderId)
    .order("leg_sequence", { ascending: true });
  if (error) throw error;
  if (!data || data.length === 0) return [];

  // legs has no FKs to tractors/drivers, so resolve relations manually.
  const tractorIds = Array.from(new Set(data.map((l) => l.assigned_tractor_id).filter(Boolean))) as string[];
  const driverIds = Array.from(
    new Set(data.flatMap((l) => [l.assigned_operator_id, l.assigned_operator_2_id]).filter(Boolean)),
  ) as string[];
  const legIds = data.map((l) => l.id);

  const [tractorsRes, driversRes, tripsRes] = await Promise.all([
    tractorIds.length
      ? supabase.from("tractors").select("*").in("id", tractorIds)
      : Promise.resolve({ data: [] as Tables<"tractors">[], error: null }),
    driverIds.length
      ? supabase.from("drivers").select("*").in("id", driverIds)
      : Promise.resolve({ data: [] as Tables<"drivers">[], error: null }),
    // Only the active trip: superseded rows (regenerated, cancelled, ...)
    // describe trips that no longer belong to the leg.
    supabase.from("trips").select("leg_id, eapi_trip_id, eapi_status").in("leg_id", legIds).eq("is_active", true),
  ]);
  if (tripsRes.error) console.warn("[useOrderLegs] active trips fetch failed:", orderId, tripsRes.error);

  const tractorsById = new Map((tractorsRes.data ?? []).map((t) => [t.id, t]));
  const driversById = new Map((driversRes.data ?? []).map((d) => [d.id, d]));
  const tripByLegId = new Map((tripsRes.data ?? []).map((t) => [t.leg_id, t]));

  return data.map((l) => {
    const trip = tripByLegId.get(l.id);
    return {
      ...l,
      assigned_tractor: l.assigned_tractor_id ? tractorsById.get(l.assigned_tractor_id) ?? null : null,
      assigned_operator: l.assigned_operator_id ? driversById.get(l.assigned_operator_id) ?? null : null,
      assigned_operator_2: l.assigned_operator_2_id ? driversById.get(l.assigned_operator_2_id) ?? null : null,
      eapi_trip_id: trip?.eapi_trip_id ?? null,
      eapi_status: trip?.eapi_status ?? null,
    };
  });
}

export function useOrderLegs(orderId: string | null | undefined, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: orderLegsQueryKey(orderId),
    queryFn: () => fetchOrderLegs(orderId!),
    enabled: !!orderId && enabled,
  });
}
